import { BookOutlined, DeleteOutlined, FileSyncOutlined } from '@ant-design/icons'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField as MuiTextField,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import Paper from 'components/@extended/Paper'
import RolloverPlanDialog from 'components/RolloverPlanDialog'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useFetchApprovalChainProfiles } from 'hooks/useApprovals'
import { usePeriods } from 'hooks/usePeriods'
import { useArchivePlan, useClosePlan } from 'hooks/usePlans'
import { useEffect, useState } from 'react'
import { canUserApproveRollover, RolloverApprovalResult } from 'types/approvals'
import type { ArchivePlanParams, ClosePlanParams } from 'types/comp-plan'
import { createMergedIdLabelMap, findPeriodById } from 'types/periods'
import { getTopParentOrganizationId, PlanMetadata, UserProfile } from 'types/user-profile'
import { DATE_FORMAT } from '../../../types/periods'
import { useEditUserDialog } from '../context/EditUserDialogContext'

dayjs.extend(utc)

interface UserPlansFormProps {
  profile: UserProfile | undefined
  user: UserProfile | undefined
  isLoadingUser: boolean
  refetchUser: () => void
  // formData: UserFormProps
  // handleFormDataChange: (newFormData: UserFormProps) => void
}

const UserPlansForm: React.FC<UserPlansFormProps> = ({ profile, user, refetchUser, isLoadingUser }) => {
  const { token } = useEditUserDialog()
  const theme = useTheme()
  const [rootOrganizationId, setRootOrganizationId] = useState<string | null>(null)
  const [rolloverModalOpen, setRolloverModalOpen] = useState(false)
  const [rolloverPlanId, setRolloverPlanId] = useState<string | null>(null)
  const [selectedPlan, setSelectedPlan] = useState<PlanMetadata | null>(null)
  const [fromPeriodLabel, setFromPeriodLabel] = useState<string>('Current Period')
  const [fromPeriodId, setFromPeriodId] = useState<string>('')
  const [finalizeModalOpen, setFinalizeModalOpen] = useState(false)
  const [archiveModalOpen, setArchiveModalOpen] = useState(false)
  const [archiveConfirmationText, setArchiveConfirmationText] = useState('')
  const [periodIdLabelMap, setPeriodIdLabelMap] = useState<Record<string, string>>({})
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({
    open: false,
    message: '',
    severity: 'success',
  })

  // Add state for finalize end date
  const [finalizeEndDate, setFinalizeEndDate] = useState<Dayjs | null>(null)
  const { archivePlan, isLoading: isArchivingPlan } = useArchivePlan(token!)
  const { closePlan, isLoading: isClosingPlan } = useClosePlan(token!)

  const planOrganizationIds = user?.plans ? [...new Set(user.plans.map((plan) => plan.organizationId))] : []

  const { approvalProfiles, isLoading: isLoadingApprovalProfiles } = useFetchApprovalChainProfiles(
    token!,
    planOrganizationIds
  )

  const {
    periods: fetchedPeriods,
    isLoadingPeriods,
    handleRefetch: refetchPeriods,
  } = usePeriods(token!, selectedPlan?.organizationId || null, !!token && !!selectedPlan?.organizationId)

  // Fetch periods for the first organization to build the period map
  const { periods: allPeriods } = usePeriods(
    token!,
    planOrganizationIds[0] || null,
    !!token && !!planOrganizationIds[0]
  )

  // Effect to update the period label when periods data changes
  useEffect(() => {
    if (selectedPlan?.periodId && fetchedPeriods?.periods && !isLoadingPeriods) {
      const periodForPlan = findPeriodById(fetchedPeriods.periods, selectedPlan.periodId)
      if (periodForPlan?.label) {
        setFromPeriodLabel(periodForPlan.label)
      } else {
        setFromPeriodLabel('Current Period')
      }
    } else if (isLoadingPeriods) {
      setFromPeriodLabel('Loading...')
    } else {
      setFromPeriodLabel('Current Period')
    }
  }, [selectedPlan, fetchedPeriods, isLoadingPeriods])

  // Effect to update the period label map when periods change
  useEffect(() => {
    if (fetchedPeriods?.periods) {
      setPeriodIdLabelMap(createMergedIdLabelMap(fetchedPeriods.periods))
    }
  }, [fetchedPeriods])

  // Effect to update the period label map for all plans
  useEffect(() => {
    if (allPeriods?.periods) {
      const mergedMap = createMergedIdLabelMap(allPeriods.periods)
      setPeriodIdLabelMap((prev) => ({ ...prev, ...mergedMap }))
    }
  }, [allPeriods])

  useEffect(() => {
    if (profile) {
      const firstRootOrgId = getTopParentOrganizationId(profile)
      setRootOrganizationId(firstRootOrgId)
    }
  }, [profile])

  const canRolloverPlan = (plan: PlanMetadata): RolloverApprovalResult => {
    if (!approvalProfiles || !profile) return { canRollover: false, reason: 'Loading approval profiles...' }

    return canUserApproveRollover(approvalProfiles, plan.organizationId, profile.user.id)
  }

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return
    }
    setSnackbar({ ...snackbar, open: false })
  }

  // Handlers for Finalize Plan
  const handleOpenFinalizeModal = (planId: string) => {
    setSelectedPlanId(planId)
    setFinalizeModalOpen(true)
  }
  const handleCloseFinalizeModal = () => {
    setFinalizeModalOpen(false)
  }
  const handleFinalizePlan = () => {
    if (!selectedPlanId || !finalizeEndDate) return

    // Find the selected plan from userProfile.plans
    const selectedPlan = user?.plans?.find((plan) => plan.id === selectedPlanId)
    if (!selectedPlan) return

    const dateString = finalizeEndDate.format('YYYY-MM-DD')
    const isoString = dayjs.utc(dateString).format('YYYY-MM-DDTHH:mm:ss.SSSZ')

    closePlan({
      planId: selectedPlanId,
      endDate: isoString,
      rootOrganizationId: selectedPlan.rootOrganizationId,
    } as ClosePlanParams)
      .then(() => {
        setSnackbar({
          open: true,
          message: 'Plan finalized successfully',
          severity: 'success',
        })
        setFinalizeModalOpen(false)

        // Refetch the target user's profile
        return refetchUser()
      })
      .catch((error) => {
        console.error('Error finalizing plan:', error)
        setSnackbar({
          open: true,
          message: 'Failed to finalize the plan',
          severity: 'error',
        })
      })
  }

  // Handlers for Archive Plan
  const handleOpenArchiveModal = (planId: string) => {
    setSelectedPlanId(planId)
    setArchiveModalOpen(true)
  }
  const handleCloseArchiveModal = () => {
    setArchiveModalOpen(false)
    setArchiveConfirmationText('')
  }
  const handleArchivePlan = () => {
    if (!selectedPlanId || !rootOrganizationId) return

    const selectedPlan = user?.plans?.find((plan) => plan.id === selectedPlanId)
    if (!selectedPlan) return

    archivePlan({
      planId: selectedPlanId,
      rootOrganizationId: selectedPlan.rootOrganizationId,
    } as ArchivePlanParams)
      .then(() => {
        setSnackbar({
          open: true,
          message: 'Plan archived successfully',
          severity: 'success',
        })
        setArchiveModalOpen(false)

        // Refetch the target user's profile
        return refetchUser()
      })
      .catch((error) => {
        console.error('Error archiving plan:', error)
        setSnackbar({
          open: true,
          message: 'Failed to archive the plan',
          severity: 'error',
        })
      })
  }

  // Update the rollover handlers
  const handleOpenRolloverModal = (planId: string) => {
    const plan = user?.plans?.find((p) => p.id === planId)
    setSelectedPlan(plan || null)
    setRolloverPlanId(planId)

    // Set the fromPeriodId
    if (plan?.periodId) {
      setFromPeriodId(plan.periodId)
    }

    // Ensure we fetch periods for the selected plan's organization
    if (plan?.organizationId) {
      refetchPeriods()
    }

    setRolloverModalOpen(true)
  }

  const handleCloseRolloverModal = () => {
    setRolloverModalOpen(false)
    setRolloverPlanId(null)
    setSelectedPlan(null)
    setFromPeriodId('')
  }

  const handleRolloverPlan = (periodId: string) => {
    // Implementation for rollover plan would go here
    console.debug('Rolling over plan with ID:', rolloverPlanId, 'to period:', periodId)
    setRolloverModalOpen(false)
    setSnackbar({
      open: true,
      message: 'Plan rollover initiated successfully',
      severity: 'success',
    })
  }

  if (isLoadingUser || !user || isLoadingApprovalProfiles) return null

  // Sort plans by effectiveDate descending
  const sortedPlans = [...(user.plans || [])].sort((a, b) => {
    const dateA = new Date(a.effectiveDate).getTime()
    const dateB = new Date(b.effectiveDate).getTime()
    return dateB - dateA // Descending order
  })

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return dayjs.utc(dateString).format(DATE_FORMAT)
  }

  // Helper function to check if plan's end date is different from period's end date
  const isPlanEndDateDifferentFromPeriod = (plan: PlanMetadata): boolean => {
    if (!plan.endDate) return false
    if (!plan.periodId || !allPeriods?.periods) return true

    const period = findPeriodById(allPeriods.periods, plan.periodId)
    if (!period?.endDate) return true

    // Compare dates only (not times)
    const planEndDate = dayjs.utc(plan.endDate).format('YYYY-MM-DD')
    const periodEndDate = dayjs.utc(period.endDate).format('YYYY-MM-DD')

    return planEndDate !== periodEndDate
  }

  return (
    <>
      <Paper>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)', minHeight: '300px' }}>
          <Table
            stickyHeader
            size="small"
            sx={{
              width: '100%',
              tableLayout: 'fixed',
              '& .MuiTableCell-stickyHeader': {
                position: 'sticky',
                top: 0,
                zIndex: 1,
              },
            }}
          >
            <TableHead
              sx={{
                '& th': {
                  borderTop: `1px solid ${theme.palette.divider}`,
                  borderBottom: `2px solid ${theme.palette.divider} !important`,
                },
              }}
            >
              <TableRow>
                <TableCell sx={{ width: 150, minWidth: 150 }}>Team</TableCell>
                <TableCell sx={{ width: 120, minWidth: 120 }}>Period</TableCell>
                <TableCell sx={{ width: 120, minWidth: 120 }}>Start Date</TableCell>
                <TableCell sx={{ width: 120, minWidth: 120 }}>End Date</TableCell>
                <TableCell sx={{ width: 150, minWidth: 150 }}>Compensation</TableCell>
                <TableCell sx={{ width: 100, minWidth: 100 }}>Variable %</TableCell>
                <TableCell sx={{ width: 120, minWidth: 120 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedPlans.map((plan) => (
                <TableRow key={plan.id}>
                  <TableCell>{plan.organizationName || 'N/A'}</TableCell>
                  <TableCell>
                    {plan.periodId && periodIdLabelMap[plan.periodId] ? periodIdLabelMap[plan.periodId] : '-'}
                  </TableCell>
                  <TableCell>{formatDate(plan.effectiveDate)}</TableCell>
                  <TableCell>{plan.endDate ? formatDate(plan.endDate) : '-'}</TableCell>
                  <TableCell>{formatCurrency(plan.baseSalary * plan.periodProration)}</TableCell>
                  <TableCell>{(plan.variablePercent * 100).toFixed(1)}%</TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      <Tooltip
                        title={
                          plan.finalized
                            ? 'Cannot finalize - plan already finalized'
                            : isPlanEndDateDifferentFromPeriod(plan)
                              ? 'Cannot finalize - plan has a custom end date'
                              : 'Finalize Plan'
                        }
                      >
                        <span>
                          <IconButton
                            size="medium"
                            sx={{ color: theme.palette.primary.main }}
                            onClick={() => handleOpenFinalizeModal(plan.id || '')}
                            disabled={plan.finalized || isPlanEndDateDifferentFromPeriod(plan)}
                          >
                            <BookOutlined />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={!canRolloverPlan(plan).canRollover ? canRolloverPlan(plan).reason : 'Rollover Plan'}
                      >
                        <span>
                          <IconButton
                            size="medium"
                            sx={{ color: theme.palette.primary.main }}
                            onClick={() => handleOpenRolloverModal(plan.id || '')}
                            disabled={!canRolloverPlan(plan).canRollover}
                          >
                            <FileSyncOutlined />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Archive Plan">
                        <span>
                          <IconButton
                            size="medium"
                            sx={{ color: theme.palette.primary.main }}
                            onClick={() => handleOpenArchiveModal(plan.id || '')}
                          >
                            <DeleteOutlined />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Finalize Plan Modal */}
      <Dialog open={finalizeModalOpen} onClose={handleCloseFinalizeModal}>
        <DialogTitle>Finalize Plan</DialogTitle>
        <DialogContent>
          <Typography>
            To finalize this plan, please select an <strong>End Date</strong> below.
          </Typography>
          <DesktopDatePicker
            label="End Date"
            value={finalizeEndDate}
            onChange={(newValue: Dayjs | null) => {
              if (newValue) {
                const dateString = newValue.format('YYYY-MM-DD')
                const utcDate = dayjs.utc(dateString)
                setFinalizeEndDate(utcDate)
              } else {
                setFinalizeEndDate(null)
              }
            }}
            format={DATE_FORMAT}
            slotProps={{
              textField: {
                variant: 'outlined',
                fullWidth: true,
                InputProps: {
                  readOnly: true,
                },
                InputLabelProps: { shrink: true },
                sx: {
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '21px',
                    '& fieldset': {
                      borderRadius: '21px',
                    },
                  },
                },
                helperText: 'Click calendar icon to select date',
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFinalizeModal} disabled={isClosingPlan}>
            Cancel
          </Button>
          <Button
            onClick={handleFinalizePlan}
            color="primary"
            disabled={!finalizeEndDate || isClosingPlan}
            startIcon={isClosingPlan ? <CircularProgress size={20} /> : null}
          >
            Finalize Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Archive Plan Modal */}
      <Dialog open={archiveModalOpen} onClose={handleCloseArchiveModal}>
        <DialogTitle>Archive Plan</DialogTitle>
        <DialogContent>
          <Typography>
            To archive this plan, please type <strong>"Archive Plan"</strong> in the box below to confirm.
          </Typography>
          <MuiTextField
            variant="outlined"
            fullWidth
            value={archiveConfirmationText}
            onChange={(e) => setArchiveConfirmationText(e.target.value)}
            placeholder="Type 'Archive Plan' to confirm"
            sx={{ mt: 2, borderRadius: '50px' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseArchiveModal} disabled={isArchivingPlan}>
            Cancel
          </Button>
          <Button
            onClick={handleArchivePlan}
            color="error"
            disabled={archiveConfirmationText !== 'Archive Plan' || isArchivingPlan}
            startIcon={isArchivingPlan ? <CircularProgress size={20} /> : null}
          >
            Archive Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rollover Plan Modal */}
      <RolloverPlanDialog
        open={rolloverModalOpen}
        onClose={handleCloseRolloverModal}
        onRollover={handleRolloverPlan}
        periods={fetchedPeriods?.periods || []}
        isLoading={isLoadingPeriods}
        theme={theme}
        fromPeriodLabel={fromPeriodLabel}
        fromPeriodId={fromPeriodId}
        plans={
          selectedPlan && selectedPlan.id
            ? [
                {
                  id: selectedPlan.id,
                  revision: selectedPlan.revision,
                  userId: user.user?.id || '',
                  templateId: selectedPlan.templateId || '',
                  employeeFirstName: user?.user.firstName || '',
                  employeeLastName: user?.user.lastName || '',
                  baseSalary: selectedPlan.baseSalary,
                  position: user?.user.position || '',
                  periodProration: selectedPlan.periodProration,
                  compensationModel: selectedPlan.compensationModel,
                  variablePercent: selectedPlan.variablePercent,
                  variableCap: selectedPlan.variableCap,
                },
              ]
            : []
        }
        periodIdLabelMap={periodIdLabelMap}
      />

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  )
}

export default UserPlansForm
